import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en";
import { useUserOwnedAssignments } from "@/store/selectors/assignmentSelectors";
import { useStyles } from "@/styles/useStyles";
import { IAssignment } from "@/types/AssignmentTypes";
import { ActionIcon, Button, Group, Space, Stack } from "@mantine/core";
import { Text } from "../ui/Text";
import { IconChevronRight, IconPlus } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useStore } from "@/store";
import { AssignmentService } from "@/services/AssignmentService";
import { SupportVideoButton } from "../support/SupportVideoButton";
import { useDefaultAssignmentPromptConfigs } from "./assignmentPromptConfigs";

TimeAgo.addDefaultLocale(en);

const timeAgo = new TimeAgo("en-US");

export function AssignmentList() {
  const userAssignments = useUserOwnedAssignments();
  const setActiveAssignmentId = useStore(
    (state) => state.partnerApp.setActiveAssignmentId,
  );

  const defaultAssignmentPromptConfigs = useDefaultAssignmentPromptConfigs();

  return (
    <Stack px="sm">
      <Button
        variant="light"
        size="sm"
        leftSection={<IconPlus />}
        fullWidth
        radius="xl"
        color="primary"
        onClick={async () => {
          const newAssignmentId =
            await AssignmentService.createNewBlankAssignment({
              defaultAssignmentPromptConfigs,
            });

          if (newAssignmentId) {
            setActiveAssignmentId(newAssignmentId);
          }
        }}
      >
        Create assignment
      </Button>

      {Object.values(userAssignments || {})
        .sort((a, b) => b.createdAt - a.createdAt)
        .map((assignment) => (
          <AssignmentListItem key={assignment.id} assignment={assignment} />
        ))}

      <Space h="md" />
      <SupportVideoButton
        supportVideoId="assignmentEducatorWalkthrough"
        shouldShowTitle
      />
    </Stack>
  );
}

function AssignmentListItem({ assignment }: { assignment: IAssignment }) {
  const { theme } = useStyles();
  const [isHovering, setIsHovering] = useState(false);

  const setActiveAssignmentId = useStore(
    (state) => state.partnerApp.setActiveAssignmentId,
  );

  const humanReadableDate = useMemo(() => {
    return timeAgo.format(new Date(assignment.createdAt));
  }, [assignment.createdAt]);

  return (
    <Stack
      bg="background.0"
      gap={0}
      p="sm"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        borderRadius: 8,
        border: `1px solid ${isHovering ? theme.colors.border[0] : theme.colors.border[0]}`,
        boxShadow: isHovering ? theme.shadows.xs : "none",
        cursor: "pointer",
      }}
      onClick={() => setActiveAssignmentId(assignment.id)}
    >
      <Group justify="space-between" align="center">
        <Stack flex={1} gap={0}>
          <Text titleStyle="h5" lh={1.1}>
            {assignment.title || "Untitled Assignment"}
          </Text>
          <Space h={4} />
          <Text size="xs" c="text.6">
            Created {humanReadableDate}
          </Text>
        </Stack>
        <ActionIcon
          variant="transparent"
          size="md"
          radius="xl"
          color={isHovering ? "primary.6" : "text.6"}
          style={{ zIndex: 100 }}
        >
          <IconChevronRight size={24} />
        </ActionIcon>
      </Group>
    </Stack>
  );
}
